import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiRefreshCw } from "react-icons/fi";
import useLivePrices from "../../hooks/useLivePrices";
import CrossoverDashboard from "../CrossoverDashboard/CrossoverDashboard";
import { useStockSnapshot } from "../../context/StockSnapshotContext";
import "./CrossoverDashboardPage.css";
const CrossoverDashboardPage = () => {
  const { symbol, securityId: paramSecurityId } = useParams();
  const navigate = useNavigate();

  const {
    stockDetail,
    stockDetailLoading,
    stockDetailError,
    getStockDetailSnapshot,
  } = useStockSnapshot();

  const { prices: livePrices, subscribeSymbols } = useLivePrices();

  // Fetch data on mount — same as Stock page
  useEffect(() => {
    if (!symbol) return;
    getStockDetailSnapshot(symbol, paramSecurityId);
    subscribeSymbols([symbol]);
  }, [symbol]);

  const handleRefresh = async () => {
    if (symbol) await getStockDetailSnapshot(symbol, paramSecurityId);
  };

  // ─── Derive data exactly like Stock page ───
  const marketSnapshot = stockDetail?.marketSnapshot;
  const quote = marketSnapshot?.quote?.data;
  const ohlc = quote?.ohlc;
  const stockName = stockDetail?.stockData;
  const live = livePrices[symbol];
  const last_price = live?.ltp || quote?.last_price;
  const close = ohlc?.close;
  const vwap = marketSnapshot?.intraday?.vwap;

  const percentChange =
    close && last_price
      ? (((last_price - close) / close) * 100).toFixed(2)
      : "0.00";
  const isPositive = Number(percentChange) >= 0;
  const priceDiff =
    last_price && close ? (last_price - close).toFixed(2) : "0.00";

  // ─── These two fields go to CrossoverDashboard ───
  const crossoverSignals = stockDetail?.crossover_signals;
  const tradesToday = stockDetail?.trades_today;

  if (stockDetailLoading) {
    return (
      <div className="cdp">
        <p className="cdp__loading">Loading {symbol} signals...</p>
      </div>
    );
  }

  if (stockDetailError) {
    return (
      <div className="cdp">
        <button className="cdp__back" onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} /> Back
        </button>
        <p className="cdp__error">{stockDetailError}</p>
      </div>
    );
  }

  return (
    <div className="cdp">
      {/* Back + Refresh */}
      <div className="cdp__header">
        <button className="cdp__back" onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} /> Back to {symbol}
        </button>
        <FiRefreshCw
          onClick={handleRefresh}
          size={16}
          style={{ cursor: "pointer", color: "#7a8ba8" }}
        />
      </div>

      {/* Price */}
      <div className="cdp__price-strip">
        <div className="cdp__price-left">
          <h2 className="cdp__company">{stockName?.company || symbol}</h2>
          <span className="cdp__symbol-tag">{symbol}</span>
        </div>
        <div className="cdp__price-right">
          <h2 className="cdp__ltp">₹{last_price || "—"}</h2>
          <span
            className={isPositive ? "cdp__change--green" : "cdp__change--red"}
          >
            {isPositive ? "▲" : "▼"} {priceDiff} ({isPositive ? "+" : ""}
            {percentChange}%)
          </span>
        </div>
      </div>

      {/* OHLC */}
      <div className="cdp__ohlc">
        <div>
          <span>Open</span>
          <b>₹{ohlc?.open || "—"}</b>
        </div>
        <div>
          <span>High</span>
          <b>₹{ohlc?.high || "—"}</b>
        </div>
        <div>
          <span>Low</span>
          <b>₹{ohlc?.low || "—"}</b>
        </div>
        <div>
          <span>Prev Close</span>
          <b>₹{close || "—"}</b>
        </div>
        <div>
          <span>VWAP</span>
          <b>₹{vwap ? Number(vwap).toFixed(2) : "—"}</b>
        </div>
      </div>

      {/* Dashboard — receives data as props */}
      <CrossoverDashboard
        symbol={symbol}
        crossoverSignals={crossoverSignals}
        tradesToday={tradesToday}
      />
    </div>
  );
};

export default CrossoverDashboardPage;
